import React, { useState, useEffect, useRef } from 'react'

import * as Yup from 'yup'
import { Formik, FieldArray, Form, Field } from 'formik'
import { toast } from 'react-toastify'

import { makeStyles } from '@material-ui/core/styles'
import { green } from '@material-ui/core/colors'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import { i18n } from '../../translate/i18n'

import api from '../../services/api'
import toastError from '../../errors/toastError'
import CountryCodeSelector from '../CountryCodeSelector'
import LeadSourceSelector from '../LeadSourceSelector'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  extraAttr: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  btnWrapper: {
    position: 'relative',
  },

  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}))

const ContactSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Digite ao menos 2 caracteres').max(50, 'Limite de 50 caracteres').required('Obrigatório'),
  number: Yup.string().min(10, 'Número inválido').max(20, 'Número inválido').required('Informe o número'),
  email: Yup.string().email('Email inválido'),
})

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles()
  const isMounted = useRef(true)

  const initialState = {
    name: '',
    number: '',
    email: '',
    disableBot: false,
    birthday: '',
    leadSource: '',
  }

  const [contact, setContact] = useState(initialState)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact(prevState => {
          return {
            ...prevState,
            ...initialValues,
          }
        })
      }

      if (!contactId) return

      try {
        const { data } = await api.get(`/contacts/${contactId}`)
        if (isMounted.current) {
          console.log(data)
          // Formata a data de aniversário para o formato YYYY-MM-DD se existir
          if (data.birthday) {
            // Se já está no formato YYYY-MM-DD, usa diretamente
            if (typeof data.birthday === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data.birthday)) {
              // Já está no formato correto
              data.birthday = data.birthday
            } else {
              // Se é uma data, extrai apenas a parte da data sem considerar timezone
              const birthdayDate = new Date(data.birthday)
              // Usa getUTCFullYear, getUTCMonth, getUTCDate para evitar problemas de timezone
              const year = birthdayDate.getUTCFullYear()
              const month = String(birthdayDate.getUTCMonth() + 1).padStart(2, '0')
              const day = String(birthdayDate.getUTCDate()).padStart(2, '0')
              data.birthday = `${year}-${month}-${day}`
            }
          }
          setContact(prevState => ({
            ...prevState,
            ...data,
          }))
        }
      } catch (err) {
        toastError(err)
      }
    }

    fetchContact()
  }, [contactId, open, initialValues])

  const handleClose = () => {
    onClose()
    setContact(initialState)
  }

  const handleSaveContact = async values => {
    try {
      const payload = { ...(values || {}) }
      delete payload.whatsapp
      if (!payload.leadSource) {
        payload.leadSource = null
      }
      if (contactId) {
        await api.put(`/contacts/${contactId}`, payload)
        handleClose()
        toast.success(i18n.t('contactModal.success'))
        return
      }

      const { data } = await api.post('/contacts', payload)

      if (onSave) {
        onSave(data)
      }
      handleClose()
      toast.success(i18n.t('contactModal.success'))
    } catch (err) {
      if (err?.response?.data?.error === 'ERR_DUPLICATED_CONTACT') {
        toast.error(i18n.t('backendErrors.ERR_DUPLICATED_CONTACT'))
        return
      }

      toastError(err)
    }
  }

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth='lg' scroll='paper'>
        <DialogTitle id='form-dialog-title'>{contactId ? `${i18n.t('contactModal.title.edit')}` : `${i18n.t('contactModal.title.add')}`}</DialogTitle>
        <Formik
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={ContactSchema}
          onSubmit={async (values, actions) => {
            await handleSaveContact(values)
            actions.setSubmitting(false)
          }}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <Typography variant='subtitle1' gutterBottom>
                  {i18n.t('contactModal.form.mainInfo')}
                </Typography>
                <Field
                  as={TextField}
                  label={i18n.t('contactModal.form.name')}
                  name='name'
                  autoFocus
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant='outlined'
                  margin='dense'
                  className={classes.textField}
                />
                <div style={{ marginBottom: 8 }}>
                  <Typography
                    variant='caption'
                    style={{
                      marginBottom: 4,
                      display: 'block',
                      color: errors.number && touched.number ? '#f44336' : 'rgba(0, 0, 0, 0.54)',
                    }}
                  >
                    {i18n.t('contactModal.form.number')}
                  </Typography>
                  <Field name='number'>
                    {({ field, form }) => (
                      <CountryCodeSelector
                        value={field.value || ''}
                        onChange={e => {
                          form.setFieldValue('number', e.target.value)
                          form.setFieldTouched('number', true)
                        }}
                        error={touched.number && Boolean(errors.number)}
                        helperText={touched.number && errors.number}
                      />
                    )}
                  </Field>
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t('contactModal.form.email')}
                    name='email'
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    placeholder='Email address'
                    fullWidth
                    margin='dense'
                    variant='outlined'
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t('contactModal.form.birthday')}
                    name='birthday'
                    type='date'
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={touched.birthday && Boolean(errors.birthday)}
                    helperText={touched.birthday && errors.birthday}
                    fullWidth
                    margin='dense'
                    variant='outlined'
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Field name='leadSource'>
                    {({ field, form }) => (
                      <LeadSourceSelector value={field.value} label={i18n.t('contactModal.form.leadSource.label')} onChange={newValue => form.setFieldValue('leadSource', newValue)} />
                    )}
                  </Field>
                </div>
                <>
                  <FormControlLabel
                    label={i18n.t('contactModal.form.disableBot')}
                    labelPlacement='start'
                    control={<Switch size='small' checked={values.disableBot} onChange={() => setContact({ ...values, disableBot: !values.disableBot })} name='disableBot' color='primary' />}
                  />
                </>
                <Typography style={{ marginBottom: 8, marginTop: 12 }} variant='subtitle1'>
                  {i18n.t('contactModal.form.extraInfo')}
                </Typography>

                <FieldArray name='extraInfo'>
                  {({ push, remove }) => (
                    <>
                      {values.extraInfo &&
                        values.extraInfo.length > 0 &&
                        values.extraInfo.map((info, index) => (
                          <div className={classes.extraAttr} key={`${index}-info`}>
                            <Field as={TextField} label={i18n.t('contactModal.form.extraName')} name={`extraInfo[${index}].name`} variant='outlined' margin='dense' className={classes.textField} />
                            <Field as={TextField} label={i18n.t('contactModal.form.extraValue')} name={`extraInfo[${index}].value`} variant='outlined' margin='dense' className={classes.textField} />
                            <IconButton size='small' onClick={() => remove(index)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </div>
                        ))}
                      <div className={classes.extraAttr}>
                        <Button style={{ flex: 1, marginTop: 8 }} variant='outlined' color='primary' onClick={() => push({ name: '', value: '' })}>
                          {`+ ${i18n.t('contactModal.buttons.addExtraInfo')}`}
                        </Button>
                      </div>
                    </>
                  )}
                </FieldArray>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color='secondary' disabled={isSubmitting} variant='outlined'>
                  {i18n.t('contactModal.buttons.cancel')}
                </Button>
                <Button type='submit' color='primary' disabled={isSubmitting} variant='contained' className={classes.btnWrapper}>
                  {contactId ? `${i18n.t('contactModal.buttons.okEdit')}` : `${i18n.t('contactModal.buttons.okAdd')}`}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  )
}

export default ContactModal
